import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Box,
  styled,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export interface DynamicColumn<T = any> {
  id: string;
  label: string;
  accessor?: keyof T | string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'right' | 'center';
  render?: (row: T, rowIndex: number) => React.ReactNode;
  /** When set, replaces the default label + optional sort icons in the header cell. */
  renderHeader?: (column: DynamicColumn<T>) => React.ReactNode;
}

export interface SortConfig {
  column: string;
  order: 'asc' | 'desc' | 'none';
}

interface DynamicTableProps<T = any> {
  columns: DynamicColumn<T>[];
  data: T[];
  sortConfigs?: SortConfig[];
  onSort?: (columnId: string) => void;
  enableSelection?: boolean;
  selectedRows?: Set<string>;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectRow?: (id: string) => void;
  getRowId?: (row: T) => string;
  noDataMessage?: string;
  rowComponent?: React.ComponentType<any>;
  variant?: 'default' | 'compact' | 'striped';
}

const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: string }>(({ theme, variant }) => ({
  border: `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.neutral1?.main || '#F6F7FB',
    fontWeight: 600,
    fontSize: '13px',
    color: theme.palette.neutral8?.main || theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.neutral3?.main || theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
    padding: variant === 'compact' ? theme.spacing(1, 1.25) : theme.spacing(1.25, 1.5),
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiTableCell-body': {
    fontSize: '13px',
    color: theme.palette.neutral8?.main || theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.neutral3?.main || theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
    padding: variant === 'compact' ? theme.spacing(1, 1.25) : theme.spacing(1.25, 1.5),
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
    borderBottom: 'none',
  },
  ...(variant === 'striped' && {
    '& .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: theme.palette.neutral1?.main || '#F6F7FB',
    },
  }),
}));

const HeaderContent = styled(Box)<{ align?: 'left' | 'right' | 'center' }>(({ align }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
}));

const SortIcon = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.neutral5?.main,
  '&.Mui-checked, &.MuiCheckbox-indeterminate': {
    color: theme.palette.themeColor?.main,
  },
}));

function DynamicTable<T = any>({
  columns,
  data,
  sortConfigs = [],
  onSort,
  enableSelection = false,
  selectedRows = new Set(),
  onSelectAll,
  onSelectRow,
  getRowId = (row: any) => row.id,
  noDataMessage = 'No data available',
  rowComponent: RowComponent,
  variant = 'default',
}: DynamicTableProps<T>) {
  const getSortOrder = (columnId: string) => {
    const sortConfig = sortConfigs.find((s) => s.column === columnId);
    return sortConfig?.order || 'none';
  };

  const getSortIcon = (columnId: string) => {
    const order = getSortOrder(columnId);
    if (order === 'asc') {
      return (
        <SortIcon>
          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        </SortIcon>
      );
    } else if (order === 'desc') {
      return (
        <SortIcon>
          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
        </SortIcon>
      );
    }
    return null;
  };

  const handleSort = (columnId: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(columnId);
    }
  };

  const allSelected = data.length > 0 && data.every((row) => selectedRows.has(getRowId(row)));
  const someSelected = data.some((row) => selectedRows.has(getRowId(row))) && !allSelected;

  return (
    <StyledTableContainer variant={variant}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {enableSelection && (
              <TableCell padding="checkbox">
                <StyledCheckbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={onSelectAll}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sx={{
                  width: column.width,
                  cursor: column.sortable && !column.renderHeader ? 'pointer' : 'default',
                }}
                onClick={() => handleSort(column.id, column.sortable && !column.renderHeader)}
              >
                <HeaderContent align={column.align}>
                  {column.renderHeader ? (
                    column.renderHeader(column)
                  ) : (
                    <>
                      {column.label}
                      {column.sortable && getSortIcon(column.id)}
                    </>
                  )}
                </HeaderContent>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + (enableSelection ? 1 : 0)} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                  {noDataMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = selectedRows.has(rowId);

              if (RowComponent) {
                return <RowComponent key={rowId} row={row} />;
              }

              return (
                <TableRow key={rowId} selected={isSelected}>
                  {enableSelection && (
                    <TableCell padding="checkbox">
                      <StyledCheckbox checked={isSelected} onChange={() => onSelectRow?.(rowId)} />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      sx={{
                        maxWidth: column.width,
                        wordBreak: 'break-word',
                      }}
                    >
                      {column.render
                        ? column.render(row, index)
                        : column.accessor
                        ? String((row as any)[column.accessor])
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
}

export default DynamicTable;
